import Stack from '@mui/material/Stack';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import React, { useCallback, useMemo } from 'react';
import { NavLink, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useIsMobile } from '../../hooks';
import { AvatarContent } from '../dataDisplay';
import { TAB_LINK_CLASS_NAME, MIN_WIDTH_FROM_TAB_COUNT } from '../../theme/constants/link';
import { persistTabSearch, readTabSearch } from '../../helpers';


const TabNavLink = ({
  id,
  label,
  path,
  quantity,
  index,
  optionsCount,
  scrollable,
  preserveSearch,
  tabSearch,
}) => {
  const {
    preserveSearchPaths = [],
    activeTabPath,
    currentSearch = '',
  } = tabSearch;
  const preserveActiveTabSearch = Boolean(
    activeTabPath && preserveSearchPaths.includes(activeTabPath),
  );
  const { path: routePath } = useRouteMatch();
  const history = useHistory();
  const isMobile = useIsMobile();
  const location = useLocation();
  const linkRoute = `${routePath}/${path}`;
  const isActive = useMemo(() => {
    if (location.pathname.includes(linkRoute)) return true;
    if (index === 0) {
      return location.pathname === routePath;
    }
    return false;
  }, [location, linkRoute, index, routePath]);
  const stringToShow = useMemo(() => {
    if (typeof label === 'object' && label.xs) {
      return isMobile ? label.xs : label.md;
    }
    return label;
  }, [label, isMobile]);

  const handleClick = useCallback(() => {
    if (!preserveActiveTabSearch || !activeTabPath) return;
    persistTabSearch(routePath, activeTabPath, history.location.search);
  }, [
    preserveActiveTabSearch,
    activeTabPath,
    history,
    routePath,
  ]);

  const to = useMemo(() => {
    if (!preserveSearch) {
      return linkRoute;
    }
    const targetSearch = path === activeTabPath
      ? currentSearch
      : readTabSearch(routePath, path);

    return {
      pathname: linkRoute,
      search: targetSearch,
    };
  }, [preserveSearch, linkRoute, routePath, path, activeTabPath, currentSearch]);

  return (
    <Link
      id={id}
      key={label}
      component={NavLink}
      to={to}
      onClick={handleClick}
      className={TAB_LINK_CLASS_NAME}
      sx={{
        [`--${MIN_WIDTH_FROM_TAB_COUNT}`]: scrollable
          ? 'max-content'
          : `calc((100% - ${optionsCount}%) / ${optionsCount})`,
        ...(scrollable && { minWidth: 'max-content', flexShrink: 0 }),
      }}
      isActive={(match) => {
        if (match) return true;
        return isActive;
      }}
      noWrap
    >
      <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" height="100%">
        <Typography
          variant="subNavItem"
        >{stringToShow}
        </Typography>
        {quantity !== undefined && !isMobile && (
        <AvatarContent content={quantity} active={isActive} />
        )}
      </Stack>
    </Link>
  );
};

TabNavLink.propTypes = {
  id: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.shape({
    xs: PropTypes.string,
    md: PropTypes.string,
  })]).isRequired,
  path: PropTypes.string.isRequired,
  quantity: PropTypes.number,
  index: PropTypes.number.isRequired,
  optionsCount: PropTypes.number.isRequired,
  preserveSearch: PropTypes.bool,
  tabSearch: PropTypes.shape({
    preserveSearchPaths: PropTypes.arrayOf(PropTypes.string),
    defaultTabPath: PropTypes.string,
    activeTabPath: PropTypes.string,
    currentSearch: PropTypes.string,
  }),
  scrollable: PropTypes.bool,
};

TabNavLink.defaultProps = {
  quantity: undefined,
  id: undefined,
  preserveSearch: false,
  tabSearch: {},
  scrollable: false,
};

export default TabNavLink;
